import {
    CroppedRegion,
    DetectedItem,
    ImageAnalysisCategoryHint,
    ImageAnalysisIssueCode,
    ImageAnalysisRequest,
    ImageAnalysisResponse,
    ImageAnalysisSelectedRegionCandidate,
    ImageAnalysisStatus,
    LookitItemCategoryId
} from './domain';
import { defaultRegionForCategory } from './category-default-regions';
import {
    ImageAnalysisProvider,
    ImageAnalysisProviderId,
    ImageAnalysisProviderRegistry,
    normalizeGarmentProviderResponse
} from './image-analysis-provider';
import { fakeImageAnalysisProvider } from './fake-image-analysis-provider';
import { guardExternalImageTransfer } from './sensitive-region-service';

export interface ImageAnalysisService {
    analyzeImage(request: ImageAnalysisRequest): Promise<ImageAnalysisResponse>;
}

export type ImageAnalysisAdapterMode = 'mock' | 'api' | 'external_ai';

export interface ImageAnalysisApiServiceOptions {
    endpoint?: string;
    fetchImpl?: ImageAnalysisFetch;
    fallbackOnNetworkError?: boolean;
    allowExternalEndpoint?: boolean;
}

export interface RuntimeImageAnalysisConfig {
    adapter: ImageAnalysisAdapterMode;
    providerId: ImageAnalysisProviderId;
    endpoint: string;
    externalProviderEnabled: boolean;
    externalProviderEndpoint: string;
    externalProviderContractReady: boolean;
    externalProviderConfigured: boolean;
    fallbackProviderId: 'mock';
}

export interface ProviderImageAnalysisServiceOptions {
    fallbackOnProviderError?: boolean;
}

export interface ExternalImageAnalysisProviderOptions {
    providerId?: ImageAnalysisProviderId;
    label?: string;
    endpoint?: string;
    enabled?: boolean;
    contractReady?: boolean;
    invoke?: (request: ImageAnalysisRequest) => Promise<unknown>;
}

export interface ImageAnalysisHttpResponse {
    ok: boolean;
    status: number;
    json?: () => Promise<unknown>;
    text?: () => Promise<string>;
}

export type ImageAnalysisFetch = (input: string, init: {
    method: string;
    headers: Record<string, string>;
    body: string;
}) => Promise<ImageAnalysisHttpResponse>;

export const defaultImageAnalysisApiEndpoint = '/api/lookit/image/analyze';

const autoCategoryMinConfidence = 0.8;
const imageAnalysisIssueCodes: ImageAnalysisIssueCode[] = [
    'no_item_detected',
    'low_confidence',
    'multiple_items_confused',
    'unsupported_image',
    'non_fashion_image'
];

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isImageAnalysisStatus(value: unknown): value is ImageAnalysisStatus {
    return value === 'detected' || imageAnalysisIssueCodes.includes(value as ImageAnalysisIssueCode);
}

function finiteNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function regionForCategory(categoryId: LookitItemCategoryId | string): CroppedRegion {
    return cloneData(defaultRegionForCategory(categoryId) || defaultRegionForCategory('other')!);
}

function usableRegion(region?: CroppedRegion | null): CroppedRegion | null {
    if (!region) return null;
    if (region.width <= 0 || region.height <= 0) return null;
    return region;
}

function detectedItemRegion(item: DetectedItem): CroppedRegion | null {
    return usableRegion(item.cropped_region) || usableRegion(item.boundingBox);
}

function categoryConfidenceForDetectedItem(item: DetectedItem): number | null {
    const confidence = item.suggestedCategoryConfidence ?? item.categoryConfidence ?? null;
    return typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : null;
}

function detectedCandidatePriority(item: DetectedItem): number {
    const confidence = typeof item.confidence === 'number' && Number.isFinite(item.confidence) ? item.confidence : 0;
    const confirmationPenalty = item.needsUserConfirmation ? 0.15 : 0;
    return confidence - confirmationPenalty;
}

function compareDetectedCandidates(a: DetectedItem, b: DetectedItem): number {
    const priorityDiff = detectedCandidatePriority(b) - detectedCandidatePriority(a);
    if (priorityDiff !== 0) return priorityDiff;
    return a.item_index - b.item_index;
}

function bestDetectedCandidate(items: DetectedItem[]): DetectedItem | null {
    return [...items]
        .filter((item) => Boolean(detectedItemRegion(item)))
        .sort(compareDetectedCandidates)[0] || null;
}

function itemNeedsConfirmation(item: DetectedItem): boolean {
    const categoryConfidence = categoryConfidenceForDetectedItem(item);
    return Boolean(
        item.needsUserConfirmation ||
        item.confidence < 0.65 ||
        categoryConfidence === null ||
        categoryConfidence < autoCategoryMinConfidence
    );
}

function issueMessage(status: ImageAnalysisStatus): string | null {
    if (status === 'no_item_detected') return '자동 감지 후보가 없어 사용자가 직접 영역을 선택해야 합니다.';
    if (status === 'low_confidence') return '자동 감지 신뢰도가 낮아 영역과 카테고리 확인이 필요합니다.';
    if (status === 'multiple_items_confused') return '여러 의류 후보가 겹쳐 사용자가 찾을 영역을 직접 확정해야 합니다.';
    if (status === 'unsupported_image') return '지원하지 않는 이미지 형식입니다. jpg, png, webp 이미지로 다시 시도해 주세요.';
    if (status === 'non_fashion_image') return '패션 상품으로 판단할 수 없는 이미지입니다. 옷이 잘 보이는 사진으로 다시 시도해 주세요.';
    return null;
}

export function fallbackImageAnalysisResponse(message: string, issueCode: ImageAnalysisIssueCode = 'low_confidence'): ImageAnalysisResponse {
    return {
        status: issueCode,
        detectedItems: [],
        suggestedRegion: null,
        suggestedCategoryId: null,
        confidence: null,
        needsUserConfirmation: true,
        issueCode,
        message
    };
}

function statusFromDetectedItems(items: DetectedItem[]): ImageAnalysisStatus {
    if (items.length === 0) return 'no_item_detected';

    const ranked = [...items].filter((item) => Boolean(detectedItemRegion(item))).sort(compareDetectedCandidates);
    const first = ranked[0] || null;
    const second = ranked[1] || null;
    if (!first) return 'no_item_detected';

    if (
        second &&
        Math.abs(detectedCandidatePriority(first) - detectedCandidatePriority(second)) < 0.04 &&
        first.confidence < 0.75
    ) {
        return 'multiple_items_confused';
    }

    return itemNeedsConfirmation(first) ? 'low_confidence' : 'detected';
}

function responseFromDetectedItems(items: DetectedItem[], forcedStatus?: ImageAnalysisStatus): ImageAnalysisResponse {
    const detectedItems = cloneData(items);
    const status = forcedStatus || statusFromDetectedItems(detectedItems);
    const bestItem = status === 'multiple_items_confused' ? null : bestDetectedCandidate(detectedItems);
    const bestRegion = bestItem ? detectedItemRegion(bestItem) : null;
    const issueCode = status === 'detected' ? null : status as ImageAnalysisIssueCode;

    return {
        status,
        detectedItems,
        suggestedRegion: bestRegion ? cloneData(bestRegion) : null,
        suggestedCategoryId: bestItem?.suggestedCategoryId || null,
        confidence: bestItem?.confidence ?? (detectedItems[0]?.confidence ?? null),
        needsUserConfirmation: status !== 'detected' || Boolean(bestItem && itemNeedsConfirmation(bestItem)),
        issueCode,
        message: issueMessage(status)
    };
}

function mockDetectedTemplates(): DetectedItem[] {
    const topRegion = regionForCategory('top');
    const bottomRegion = regionForCategory('bottom');
    const shoesRegion = regionForCategory('shoes');

    return [
        {
            id: 'mock-detected-top-blouse',
            item_index: 0,
            label: '상의 > 블라우스',
            category: 'top',
            confidence: 0.86,
            suggestedCategoryId: 'top',
            suggestedCategoryConfidence: 0.84,
            categoryConfidence: 0.84,
            boundingBox: topRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: false,
            confirmationReason: null,
            colors: ['크림'],
            cropped_region: topRegion
        },
        {
            id: 'mock-detected-bottom-pants',
            item_index: 1,
            label: '하의 > 데님',
            category: 'bottom',
            confidence: 0.79,
            suggestedCategoryId: 'bottom',
            suggestedCategoryConfidence: 0.76,
            categoryConfidence: 0.76,
            boundingBox: bottomRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: false,
            confirmationReason: null,
            colors: ['중청'],
            cropped_region: bottomRegion
        },
        {
            id: 'mock-detected-shoes',
            item_index: 2,
            label: '신발',
            category: 'shoes',
            confidence: 0.52,
            suggestedCategoryId: 'shoes',
            suggestedCategoryConfidence: 0.46,
            categoryConfidence: 0.46,
            boundingBox: shoesRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'low_confidence',
            colors: ['블랙'],
            cropped_region: shoesRegion
        }
    ];
}

function mockLowConfidenceItems(): DetectedItem[] {
    const bottomRegion = regionForCategory('bottom');

    return [
        {
            id: 'mock-low-confidence-bottom',
            item_index: 0,
            label: '하의 후보',
            category: 'bottom',
            confidence: 0.58,
            suggestedCategoryId: 'bottom',
            suggestedCategoryConfidence: 0.42,
            categoryConfidence: 0.42,
            boundingBox: bottomRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'low_confidence',
            colors: ['중청'],
            cropped_region: bottomRegion
        }
    ];
}

function mockMultipleConfusedItems(): DetectedItem[] {
    const topRegion = regionForCategory('top');
    const outerRegion = regionForCategory('outer');

    return [
        {
            id: 'mock-confused-top',
            item_index: 0,
            label: '상의 후보',
            category: 'top',
            confidence: 0.62,
            suggestedCategoryId: 'top',
            suggestedCategoryConfidence: 0.56,
            categoryConfidence: 0.56,
            boundingBox: topRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'multi_item_overlap',
            colors: ['크림'],
            cropped_region: topRegion
        },
        {
            id: 'mock-confused-outer',
            item_index: 1,
            label: '아우터 후보',
            category: 'outer',
            confidence: 0.61,
            suggestedCategoryId: 'outer',
            suggestedCategoryConfidence: 0.55,
            categoryConfidence: 0.55,
            boundingBox: outerRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'multi_item_overlap',
            colors: ['아이보리'],
            cropped_region: outerRegion
        }
    ];
}

function scenarioText(request: ImageAnalysisRequest): string {
    return [
        request.localMockImageId || '',
        request.uploadImageId || '',
        request.cropImageId || '',
        request.fileName || ''
    ].join(' ').toLowerCase();
}

function bestRegionCandidate(candidates: ImageAnalysisSelectedRegionCandidate[] = []): ImageAnalysisSelectedRegionCandidate | null {
    return [...candidates]
        .filter((candidate) => Boolean(usableRegion(candidate.region)))
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0] || null;
}

function bestCategoryHint(hints: ImageAnalysisCategoryHint[] = []): ImageAnalysisCategoryHint | null {
    return [...hints]
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0] || null;
}

function normalizedCategoryHint(hint?: ImageAnalysisRequest['categoryHint']): ImageAnalysisCategoryHint | null {
    if (!hint) return null;
    if (typeof hint === 'string') {
        return {
            categoryId: hint,
            source: 'user_selected',
            confidence: null
        };
    }

    if (isRecord(hint) && typeof hint.categoryId === 'string') {
        return hint as ImageAnalysisCategoryHint;
    }

    return null;
}

function categoryHintForRequest(request: ImageAnalysisRequest): ImageAnalysisCategoryHint | null {
    return normalizedCategoryHint(request.categoryHint)
        || (request.selectedCategoryId
            ? {
                categoryId: request.selectedCategoryId,
                source: 'user_selected',
                confidence: request.userConfirmed ? 1 : null
            }
            : null)
        || bestCategoryHint(request.categoryHints);
}

function responseFromRegionCandidate(request: ImageAnalysisRequest): ImageAnalysisResponse | null {
    const hint = categoryHintForRequest(request);
    const directRegion = usableRegion(request.selectedRegion);
    const candidate = directRegion
        ? {
            id: 'selected-region',
            region: directRegion,
            source: 'manual' as const,
            categoryId: hint?.categoryId || null,
            confidence: request.userConfirmed ? 0.92 : hint?.confidence ?? 0.82
        }
        : bestRegionCandidate(request.selectedRegionCandidates);
    if (!candidate) return null;

    const confidence = candidate.confidence ?? hint?.confidence ?? 0.5;
    const suggestedCategoryId = candidate.categoryId || hint?.categoryId || null;
    const status: ImageAnalysisStatus = confidence >= autoCategoryMinConfidence ? 'detected' : 'low_confidence';
    const issueCode = status === 'detected' ? null : status as ImageAnalysisIssueCode;

    return {
        status,
        detectedItems: mockDetectedTemplates(),
        suggestedRegion: cloneData(candidate.region),
        suggestedCategoryId,
        confidence,
        needsUserConfirmation: status !== 'detected',
        issueCode,
        message: issueMessage(status)
    };
}

export function createMockImageAnalysisResponseFromDetectedItems(detectedItems: DetectedItem[]): ImageAnalysisResponse {
    return responseFromDetectedItems(detectedItems);
}

export async function analyzeMockImage(request: ImageAnalysisRequest): Promise<ImageAnalysisResponse> {
    return fakeImageAnalysisProvider.detectItems(request);
}

export const mockImageAnalysisService: ImageAnalysisService = {
    analyzeImage: analyzeMockImage
};

export const mockImageAnalysisProvider: ImageAnalysisProvider = fakeImageAnalysisProvider;

function setJsonParam(params: URLSearchParams, key: string, value: unknown) {
    if (value === undefined || value === null) return;
    params.set(key, JSON.stringify(value));
}

export function createImageAnalysisApiRequestBody(request: ImageAnalysisRequest): URLSearchParams {
    const params = new URLSearchParams();
    if (request.uploadImageId) params.set('uploadImageId', request.uploadImageId);
    if (request.cropImageId) params.set('cropImageId', request.cropImageId);
    if (request.cropUrl) params.set('cropUrl', request.cropUrl);
    if (request.localMockImageId) params.set('localMockImageId', request.localMockImageId);
    if (request.imageUrl && !request.uploadImageId) params.set('imageUrl', request.imageUrl);
    if (typeof request.fileSize === 'number' && Number.isFinite(request.fileSize)) params.set('fileSize', String(request.fileSize));
    setJsonParam(params, 'imageSize', request.imageSize);
    setJsonParam(params, 'selectedRegion', request.selectedRegion);
    setJsonParam(params, 'categoryHint', request.categoryHint);
    setJsonParam(params, 'cropRegionOriginal', request.cropRegionOriginal);
    if (request.selectedCategoryId) params.set('selectedCategoryId', request.selectedCategoryId);
    if (request.regionSource) params.set('regionSource', request.regionSource);
    if (typeof request.userConfirmed === 'boolean') params.set('userConfirmed', request.userConfirmed ? 'true' : 'false');
    setJsonParam(params, 'selectedRegionCandidates', request.selectedRegionCandidates || []);
    setJsonParam(params, 'categoryHints', request.categoryHints || []);
    setJsonParam(params, 'privacyFlags', request.privacyFlags);
    return params;
}

function payloadRecord(payload: unknown): Record<string, unknown> | null {
    if (!isRecord(payload)) return null;
    if (isRecord(payload.data)) {
        if (typeof payload.data.status === 'string') return payload.data;
        if (isRecord(payload.data.imageAnalysis)) return payload.data.imageAnalysis;
    }
    if (typeof payload.status === 'string') return payload;
    if (isRecord(payload.imageAnalysis)) return payload.imageAnalysis;
    return null;
}

function responseFromApiPayload(payload: unknown): ImageAnalysisResponse | null {
    const record = payloadRecord(payload);
    if (!record) return null;
    if (
        !isImageAnalysisStatus(record.status) &&
        !Array.isArray(record.detectedItems) &&
        !Array.isArray(record.items) &&
        !Array.isArray(record.garments)
    ) {
        return null;
    }
    return normalizeGarmentProviderResponse(record, { providerId: 'api', defaultSource: 'ai_model' });
}

async function readJsonPayload(response: ImageAnalysisHttpResponse): Promise<unknown> {
    if (typeof response.json === 'function') {
        return response.json();
    }

    if (typeof response.text === 'function') {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
    }

    return null;
}

function runtimeFetch(): ImageAnalysisFetch | null {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as { fetch?: unknown };
    return typeof scope?.fetch === 'function' ? (scope.fetch as ImageAnalysisFetch) : null;
}

function isAbsoluteHttpEndpoint(endpoint: string): boolean {
    return /^https?:\/\//i.test(endpoint);
}

export function createApiImageAnalysisService(options: ImageAnalysisApiServiceOptions = {}): ImageAnalysisService {
    const endpoint = options.endpoint || defaultImageAnalysisApiEndpoint;
    const fetchImpl = options.fetchImpl || runtimeFetch();
    const fallbackOnNetworkError = options.fallbackOnNetworkError !== false;

    return {
        async analyzeImage(request: ImageAnalysisRequest): Promise<ImageAnalysisResponse> {
            if (isAbsoluteHttpEndpoint(endpoint) && !options.allowExternalEndpoint) {
                return fallbackImageAnalysisResponse('외부 이미지 분석 endpoint는 provider registry와 외부 전송 guard 없이 호출할 수 없습니다.');
            }

            if (!fetchImpl) {
                return fallbackImageAnalysisResponse('이미지 분석 API 호출 환경이 없어 수동 영역 선택으로 전환합니다.');
            }

            try {
                const body = createImageAnalysisApiRequestBody(request);
                const response = await fetchImpl(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                    },
                    body: body.toString()
                });
                const payload = await readJsonPayload(response);
                const apiResponse = responseFromApiPayload(payload);
                if (apiResponse) return apiResponse;

                return fallbackImageAnalysisResponse(`이미지 분석 API 응답을 해석할 수 없어 수동 영역 선택으로 전환합니다. (${response.status})`);
            } catch (error) {
                if (!fallbackOnNetworkError) throw error;
                return fallbackImageAnalysisResponse('이미지 분석 API 연결에 실패해 수동 영역 선택으로 전환합니다.');
            }
        }
    };
}

export function createApiImageAnalysisProvider(options: ImageAnalysisApiServiceOptions = {}): ImageAnalysisProvider {
    const service = createApiImageAnalysisService(options);
    return {
        id: 'api',
        label: 'LOOKIT internal image analysis API provider',
        kind: 'internal_api',
        capabilities: ['garment_analysis'],
        requiresExternalTransferGuard: false,
        detectItems: (request) => service.analyzeImage(request),
        analyzeGarments: (request) => service.analyzeImage(request)
    };
}

export function createExternalImageAnalysisProvider(options: ExternalImageAnalysisProviderOptions = {}): ImageAnalysisProvider {
    const providerId = options.providerId || 'external_ai';
    return {
        id: providerId,
        label: options.label || 'External AI image analysis provider',
        kind: 'external_ai',
        capabilities: ['garment_analysis', 'sensitive_region_detection', 'garment_segmentation'],
        requiresExternalTransferGuard: true,
        async detectItems(request: ImageAnalysisRequest): Promise<unknown> {
            if (!options.enabled || !options.endpoint || !options.contractReady || !options.invoke) {
                throw new Error('External image analysis provider is not configured.');
            }

            return options.invoke(request);
        },
        async analyzeGarments(request: ImageAnalysisRequest): Promise<unknown> {
            if (!options.enabled || !options.endpoint || !options.contractReady || !options.invoke) {
                throw new Error('External image analysis provider is not configured.');
            }

            return options.invoke(request);
        }
    };
}

function externalTransferGuardInputFromRequest(request: ImageAnalysisRequest) {
    return {
        purpose: 'external_ai_analysis' as const,
        hasExternalTransferConsent: request.hasExternalTransferConsent ?? request.privacyFlags?.allowExternalAnalysis === true,
        externalTransferReady: request.externalTransferReady,
        sensitiveDetectionStatus: request.sensitiveDetectionStatus ?? null,
        sensitiveRegionCandidates: request.sensitiveRegionCandidates || [],
        cropImageId: request.cropImageId || null,
        cropUrl: request.cropUrl || null,
        selectedCrop: request.selectedCrop || null
    };
}

export function createProviderImageAnalysisService(
    provider: ImageAnalysisProvider,
    options: ProviderImageAnalysisServiceOptions = {}
): ImageAnalysisService {
    const fallbackOnProviderError = options.fallbackOnProviderError !== false;

    return {
        async analyzeImage(request: ImageAnalysisRequest): Promise<ImageAnalysisResponse> {
            if (provider.requiresExternalTransferGuard || provider.kind === 'external_ai') {
                const guard = guardExternalImageTransfer(externalTransferGuardInputFromRequest(request));
                if (!guard.allowed) {
                    return fallbackImageAnalysisResponse(guard.message);
                }
            }

            try {
                const payload = await provider.detectItems(request);
                return normalizeGarmentProviderResponse(payload, {
                    providerId: provider.id,
                    defaultSource: provider.kind === 'mock' ? 'mock' : 'ai_model'
                });
            } catch (error) {
                if (!fallbackOnProviderError) throw error;
                return fallbackImageAnalysisResponse('이미지 분석 provider 연결에 실패해 수동 영역 선택으로 전환합니다.');
            }
        }
    };
}

export function createImageAnalysisProviderRegistry(config?: RuntimeImageAnalysisConfig): ImageAnalysisProviderRegistry {
    const registry = new ImageAnalysisProviderRegistry();
    const runtimeConfig = config || runtimeImageAnalysisConfig();

    registry.register(mockImageAnalysisProvider);
    registry.register(createApiImageAnalysisProvider({ endpoint: runtimeConfig.endpoint }));
    registry.register(createExternalImageAnalysisProvider({
        providerId: 'external_ai',
        endpoint: runtimeConfig.externalProviderEndpoint,
        enabled: runtimeConfig.externalProviderEnabled,
        contractReady: runtimeConfig.externalProviderContractReady
    }));

    return registry;
}

type RuntimeScope = {
    LOOKIT_IMAGE_ANALYSIS_ADAPTER?: unknown;
    LOOKIT_IMAGE_ANALYSIS_ENDPOINT?: unknown;
    LOOKIT_IMAGE_ANALYSIS_PROVIDER?: unknown;
    LOOKIT_ENABLE_EXTERNAL_IMAGE_ANALYSIS?: unknown;
    LOOKIT_EXTERNAL_IMAGE_ANALYSIS_ENDPOINT?: unknown;
    LOOKIT_EXTERNAL_IMAGE_ANALYSIS_CONTRACT_READY?: unknown;
    __LOOKIT_CONFIG__?: {
        imageAnalysisAdapter?: unknown;
        imageAnalysisProvider?: unknown;
        imageAnalysisEndpoint?: unknown;
        imageAnalysisApiEndpoint?: unknown;
        imageAnalysisExternalProviderEnabled?: unknown;
        externalImageAnalysisEnabled?: unknown;
        imageAnalysisExternalProviderEndpoint?: unknown;
        externalImageAnalysisEndpoint?: unknown;
        imageAnalysisExternalProviderContractReady?: unknown;
        externalImageAnalysisContractReady?: unknown;
    };
    process?: {
        env?: Record<string, string | undefined>;
    };
    localStorage?: {
        getItem(key: string): string | null;
    };
};

function normalizedAdapterMode(value: unknown): ImageAnalysisAdapterMode | null {
    return value === 'api' || value === 'mock' || value === 'external_ai' ? value : null;
}

function normalizedProviderId(value: unknown): ImageAnalysisProviderId | null {
    return value === 'api' || value === 'mock' || value === 'external_ai' ? value : null;
}

function truthyFlag(value: unknown): boolean {
    if (value === true) return true;
    if (typeof value === 'string') return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
    return false;
}

function envValue(scope: RuntimeScope | null, key: string): string | null {
    return scope?.process?.env?.[key] || null;
}

function safeLocalStorageGet(scope: RuntimeScope | null, key: string): string | null {
    try {
        return scope?.localStorage?.getItem(key) || null;
    } catch {
        return null;
    }
}

export function runtimeImageAnalysisConfig(): RuntimeImageAnalysisConfig {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as RuntimeScope;
    const appConfig = scope?.__LOOKIT_CONFIG__;
    const providerId = normalizedProviderId(scope?.LOOKIT_IMAGE_ANALYSIS_PROVIDER)
        || normalizedProviderId(envValue(scope, 'LOOKIT_IMAGE_ANALYSIS_PROVIDER'))
        || normalizedProviderId(appConfig?.imageAnalysisProvider)
        || normalizedProviderId(safeLocalStorageGet(scope, 'lookit.imageAnalysisProvider'))
        || normalizedProviderId(scope?.LOOKIT_IMAGE_ANALYSIS_ADAPTER)
        || normalizedProviderId(envValue(scope, 'LOOKIT_IMAGE_ANALYSIS_ADAPTER'))
        || normalizedProviderId(appConfig?.imageAnalysisAdapter)
        || normalizedProviderId(safeLocalStorageGet(scope, 'lookit.imageAnalysisAdapter'))
        || 'mock';
    const adapter = normalizedAdapterMode(providerId)
        || normalizedAdapterMode(scope?.LOOKIT_IMAGE_ANALYSIS_ADAPTER)
        || normalizedAdapterMode(appConfig?.imageAnalysisAdapter)
        || normalizedAdapterMode(safeLocalStorageGet(scope, 'lookit.imageAnalysisAdapter'))
        || 'mock';
    const endpoint = (
        typeof scope?.LOOKIT_IMAGE_ANALYSIS_ENDPOINT === 'string' && scope.LOOKIT_IMAGE_ANALYSIS_ENDPOINT
    ) || envValue(scope, 'LOOKIT_IMAGE_ANALYSIS_ENDPOINT') || (
        typeof appConfig?.imageAnalysisEndpoint === 'string' && appConfig.imageAnalysisEndpoint
    ) || (
        typeof appConfig?.imageAnalysisApiEndpoint === 'string' && appConfig.imageAnalysisApiEndpoint
    ) || safeLocalStorageGet(scope, 'lookit.imageAnalysisEndpoint')
        || defaultImageAnalysisApiEndpoint;
    const externalProviderEnabled = truthyFlag(scope?.LOOKIT_ENABLE_EXTERNAL_IMAGE_ANALYSIS)
        || truthyFlag(envValue(scope, 'LOOKIT_ENABLE_EXTERNAL_IMAGE_ANALYSIS'))
        || truthyFlag(appConfig?.imageAnalysisExternalProviderEnabled)
        || truthyFlag(appConfig?.externalImageAnalysisEnabled)
        || truthyFlag(safeLocalStorageGet(scope, 'lookit.externalImageAnalysisEnabled'));
    const externalProviderEndpoint = (
        typeof scope?.LOOKIT_EXTERNAL_IMAGE_ANALYSIS_ENDPOINT === 'string' && scope.LOOKIT_EXTERNAL_IMAGE_ANALYSIS_ENDPOINT
    ) || envValue(scope, 'LOOKIT_EXTERNAL_IMAGE_ANALYSIS_ENDPOINT') || (
        typeof appConfig?.imageAnalysisExternalProviderEndpoint === 'string' && appConfig.imageAnalysisExternalProviderEndpoint
    ) || (
        typeof appConfig?.externalImageAnalysisEndpoint === 'string' && appConfig.externalImageAnalysisEndpoint
    ) || safeLocalStorageGet(scope, 'lookit.externalImageAnalysisEndpoint')
        || '';
    const externalProviderContractReady = truthyFlag(scope?.LOOKIT_EXTERNAL_IMAGE_ANALYSIS_CONTRACT_READY)
        || truthyFlag(envValue(scope, 'LOOKIT_EXTERNAL_IMAGE_ANALYSIS_CONTRACT_READY'))
        || truthyFlag(appConfig?.imageAnalysisExternalProviderContractReady)
        || truthyFlag(appConfig?.externalImageAnalysisContractReady)
        || truthyFlag(safeLocalStorageGet(scope, 'lookit.externalImageAnalysisContractReady'));
    const externalProviderConfigured = Boolean(
        externalProviderEnabled &&
        externalProviderEndpoint &&
        externalProviderContractReady
    );

    return {
        adapter,
        providerId,
        endpoint,
        externalProviderEnabled,
        externalProviderEndpoint,
        externalProviderContractReady,
        externalProviderConfigured,
        fallbackProviderId: 'mock'
    };
}

export function createRuntimeImageAnalysisService(): ImageAnalysisService {
    const config = runtimeImageAnalysisConfig();
    if (config.providerId === 'external_ai' && !config.externalProviderConfigured) {
        return mockImageAnalysisService;
    }

    const registry = createImageAnalysisProviderRegistry(config);
    const provider = registry.resolve(config.providerId, config.fallbackProviderId) || mockImageAnalysisProvider;
    if (provider.id === 'mock') return mockImageAnalysisService;
    return createProviderImageAnalysisService(provider);
}
